import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { ErrorResponse } from '@percent/workplace-giving/api/goodstack'
import { useErrorModal, useModal, useSuccessModal } from '@percent/domain/giving'
import { PublishCampaignModal } from './PublishCampaign/PublishCampaignModal'
import { PublishCampaignSuccessModal } from './PublishCampaign/SuccessModal'
import { PublishCampaignFailureModal } from './PublishCampaign/FailureModal'
import { UnpublishCampaignModal } from './UnpublishCampaign/UnpublishCampaignModal'
import { UnpublishCampaignSuccessModal } from './UnpublishCampaign/SuccessModal'
import { UnpublishCampaignFailureModal } from './UnpublishCampaign/FailureModal'
import { CancelCampaignModal } from './CancelCampaign/CancelCampaignModal'
import { CancelCampaignSuccessModal } from './CancelCampaign/SuccessModal'
import { CancelCampaignFailureModal } from './CancelCampaign/FailureModal'

type Campaign = { id: string; title: string }

export type CampaignActionsModalsContextType = Readonly<{
  publishCampaign: (campaign: Campaign) => void
  unpublishCampaign: (campaign: Campaign) => void
  cancelCampaign: (campaign: Campaign) => void
}>

const CampaignActionsModalsContext = createContext<CampaignActionsModalsContextType | null>(null)

export type CampaignActionsModalsProviderProps = Readonly<{
  refetch: () => void
}>

const errorCodesMap = {
  'giving_campaigns/wrong_state': 'workplace_giving.myCampaigns.actions.apiErrors.wrongState'
} as const

export function CampaignActionsModalsProvider({
  children,
  refetch
}: PropsWithChildren<CampaignActionsModalsProviderProps>) {
  const { t } = useTranslation()

  const [publishingCampaign, setPublishingCampaign] = useState<Campaign | null>(null)
  const publishCampaignConfirmationModal = useModal()
  const publishCampaignSuccessModal = useSuccessModal()
  const publishCampaignErrorModal = useErrorModal()

  const publishCampaign = useCallback(
    (campaign: Campaign) => {
      setPublishingCampaign(campaign)
      publishCampaignConfirmationModal.openModal()
    },
    [setPublishingCampaign, publishCampaignConfirmationModal]
  )

  const onPublishCampaignSuccess = useCallback(() => {
    setPublishingCampaign(null)
    publishCampaignConfirmationModal.closeModal()
    publishCampaignSuccessModal.openModal()
  }, [setPublishingCampaign, publishCampaignConfirmationModal, publishCampaignSuccessModal])
  const closePublishCampaignSuccessModal = useCallback(() => {
    publishCampaignSuccessModal.closeModal()
    refetch()
  }, [publishCampaignSuccessModal, refetch])

  const onPublishCampaignError = useCallback(
    (error?: ErrorResponse | unknown) => {
      const errorCode = 'result' in (error as ErrorResponse) ? (error as ErrorResponse).error.code : undefined
      const mappedErrorCode =
        errorCode && Object.keys(errorCodesMap).includes(errorCode)
          ? errorCodesMap[errorCode as keyof typeof errorCodesMap]
          : undefined

      setPublishingCampaign(null)
      publishCampaignConfirmationModal.closeModal()
      publishCampaignErrorModal.setErrorMsg(t(mappedErrorCode ?? 'workplace_giving.errors.unexpected'))
      publishCampaignErrorModal.openModal()
    },
    [setPublishingCampaign, publishCampaignConfirmationModal, publishCampaignErrorModal, t]
  )

  const [unpublishingCampaign, setUnpublishingCampaign] = useState<Campaign | null>(null)
  const unpublishCampaignConfirmationModal = useModal()
  const unpublishCampaignSuccessModal = useSuccessModal()
  const unpublishCampaignErrorModal = useErrorModal()

  const unpublishCampaign = useCallback(
    (campaign: Campaign) => {
      setUnpublishingCampaign(campaign)
      unpublishCampaignConfirmationModal.openModal()
    },
    [setUnpublishingCampaign, unpublishCampaignConfirmationModal]
  )

  const onUnpublishCampaignSuccess = useCallback(() => {
    setUnpublishingCampaign(null)
    unpublishCampaignConfirmationModal.closeModal()
    unpublishCampaignSuccessModal.openModal()
  }, [setUnpublishingCampaign, unpublishCampaignConfirmationModal, unpublishCampaignSuccessModal])
  const closeUnpublishCampaignSuccessModal = useCallback(() => {
    unpublishCampaignSuccessModal.closeModal()
    refetch()
  }, [unpublishCampaignSuccessModal, refetch])

  const onUnpublishCampaignError = useCallback(
    (error?: ErrorResponse | unknown) => {
      const errorCode = 'result' in (error as ErrorResponse) ? (error as ErrorResponse).error.code : undefined
      const mappedErrorCode =
        errorCode && Object.keys(errorCodesMap).includes(errorCode)
          ? errorCodesMap[errorCode as keyof typeof errorCodesMap]
          : undefined

      setUnpublishingCampaign(null)
      unpublishCampaignConfirmationModal.closeModal()
      unpublishCampaignErrorModal.setErrorMsg(t(mappedErrorCode ?? 'workplace_giving.errors.unexpected'))
      unpublishCampaignErrorModal.openModal()
    },
    [setUnpublishingCampaign, unpublishCampaignConfirmationModal, unpublishCampaignErrorModal, t]
  )

  const [cancellingCampaign, setCancelingCampaign] = useState<Campaign | null>(null)
  const cancelCampaignConfirmationModal = useModal()
  const cancelCampaignSuccessModal = useSuccessModal()
  const cancelCampaignErrorModal = useErrorModal()

  const cancelCampaign = useCallback(
    (campaign: Campaign) => {
      setCancelingCampaign(campaign)
      cancelCampaignConfirmationModal.openModal()
    },
    [setCancelingCampaign, cancelCampaignConfirmationModal]
  )

  const onCancelCampaignSuccess = useCallback(() => {
    setCancelingCampaign(null)
    cancelCampaignConfirmationModal.closeModal()
    cancelCampaignSuccessModal.openModal()
  }, [setCancelingCampaign, cancelCampaignConfirmationModal, cancelCampaignSuccessModal])
  const closeCancelCampaignSuccessModal = useCallback(() => {
    cancelCampaignSuccessModal.closeModal()
    refetch()
  }, [cancelCampaignSuccessModal, refetch])

  const onCancelCampaignError = useCallback(
    (error?: ErrorResponse | unknown) => {
      const errorCode = 'result' in (error as ErrorResponse) ? (error as ErrorResponse).error.code : undefined
      const mappedErrorCode =
        errorCode && Object.keys(errorCodesMap).includes(errorCode)
          ? errorCodesMap[errorCode as keyof typeof errorCodesMap]
          : undefined

      setCancelingCampaign(null)
      cancelCampaignConfirmationModal.closeModal()
      cancelCampaignErrorModal.setErrorMsg(t(mappedErrorCode ?? 'workplace_giving.errors.unexpected'))
      cancelCampaignErrorModal.openModal()
    },
    [setCancelingCampaign, cancelCampaignConfirmationModal, cancelCampaignErrorModal, t]
  )

  const value = useMemo(
    () => ({
      publishCampaign,
      unpublishCampaign,
      cancelCampaign
    }),
    [publishCampaign, unpublishCampaign, cancelCampaign]
  )

  return (
    <CampaignActionsModalsContext.Provider value={value}>
      {children}
      <PublishCampaignModal
        {...publishCampaignConfirmationModal}
        givingCampaign={publishingCampaign}
        onSuccess={onPublishCampaignSuccess}
        onError={onPublishCampaignError}
      />
      <PublishCampaignSuccessModal {...publishCampaignSuccessModal} closeModal={closePublishCampaignSuccessModal} />
      <PublishCampaignFailureModal {...publishCampaignErrorModal} />
      <UnpublishCampaignModal
        {...unpublishCampaignConfirmationModal}
        givingCampaign={unpublishingCampaign}
        onSuccess={onUnpublishCampaignSuccess}
        onError={onUnpublishCampaignError}
      />
      <UnpublishCampaignSuccessModal
        {...unpublishCampaignSuccessModal}
        closeModal={closeUnpublishCampaignSuccessModal}
      />
      <UnpublishCampaignFailureModal {...unpublishCampaignErrorModal} />
      <CancelCampaignModal
        {...cancelCampaignConfirmationModal}
        givingCampaign={cancellingCampaign}
        onSuccess={onCancelCampaignSuccess}
        onError={onCancelCampaignError}
      />
      <CancelCampaignSuccessModal {...cancelCampaignSuccessModal} closeModal={closeCancelCampaignSuccessModal} />
      <CancelCampaignFailureModal {...cancelCampaignErrorModal} />
    </CampaignActionsModalsContext.Provider>
  )
}

export const useCampaignActionsModals = () => {
  const context = useContext(CampaignActionsModalsContext)

  if (!context) {
    throw new Error('useCampaignActionsModals must be used within a CampaignActionsModalsProvider')
  }

  return context
}
